import { CheckCircleRounded, CloseRounded } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  ThemeProvider,
  Typography,
  useTheme,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getAllTruckImplementsByCompanyType } from 'app/entities/truck-implement/truck-implement.reducer';
import { IRepresentativeCompanyAndTruck } from 'app/shared/model/dto/representative-company-and-truck.model';
import { Loading } from 'app/shared/util/loading';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { translate } from 'react-jhipster';

interface IRepresentativeCompanyAndTruckModalProps {
  handleSave: (data: { representativeCompanies: any[]; representativeTrucks: any[] }) => void;
  toggleModal: () => void;
  representativeCompanies: number[];
  representativeTruckImplements: number[];
  selectedType: any;
  groupByCompany?: boolean;
  title?: string;
}

export const RepresentativeCompanyAndTruckModal = ({
  handleSave,
  toggleModal,
  representativeTruckImplements,
  groupByCompany = true,
  title,
}: IRepresentativeCompanyAndTruckModalProps) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const truckImplementList = useAppSelector(state => state.truckImplement.entitiesAlt as IRepresentativeCompanyAndTruck[]);
  const loading = useAppSelector(state => state.truckImplement.loading);

  const [selectedTrucks, setSelectedTrucks] = useState(
    representativeTruckImplements ? new Set(representativeTruckImplements.map(representative => representative)) : new Set()
  );
  const [debouncedCompanyFilterValue, setDebouncedCompanyFilterValue] = useState('');
  const [debouncedTruckFilterValue, setDebouncedTruckFilterValue] = useState('');
  const [companyFilterValue, setCompanyFilterValue] = useState('');
  const [truckFilterValue, setTruckFilterValue] = useState('');
  const [filtering, setFiltering] = useState(false);

  const debouncedSetCompanyFilterValue = useCallback(
    debounce(value => {
      setDebouncedCompanyFilterValue(value);
      setFiltering(false);
    }, 1000),
    []
  );

  const debouncedSetTruckFilterValue = useCallback(
    debounce(value => {
      setDebouncedTruckFilterValue(value);
      setFiltering(false);
    }, 1000),
    []
  );

  const handleCompanyInputChange = event => {
    const { value } = event.target;

    if (value) {
      setCompanyFilterValue(value);
      setFiltering(true);
      debouncedSetCompanyFilterValue(value);
    } else {
      setCompanyFilterValue('');
      setDebouncedCompanyFilterValue('');
    }
  };

  const handleTruckInputChange = event => {
    const { value } = event.target;

    if (value) {
      setTruckFilterValue(value);
      setFiltering(true);
      debouncedSetTruckFilterValue(value);
    } else {
      setTruckFilterValue('');
      setDebouncedTruckFilterValue('');
    }
  };

  useEffect(() => {
    dispatch(getAllTruckImplementsByCompanyType());
  }, []);

  const groupedAndSortedTrucks = useMemo(() => {
    const filteredTrucks = truckImplementList.filter(
      truck =>
        truck.companyName.toLowerCase().includes(debouncedCompanyFilterValue.toLowerCase()) &&
        truck.licensePlate.toLowerCase().includes(debouncedTruckFilterValue.toLowerCase())
    );

    const groupedByCompany = filteredTrucks.reduce((acc, truck) => {
      const companyName = truck.companyName || 'Sem Empresa';
      if (!acc[companyName]) {
        acc[companyName] = [];
      }
      acc[companyName].push(truck);
      return acc;
    }, {});

    const sortedCompanyNames = Object.keys(groupedByCompany).sort((a, b) => {
      const hasRepresentedTruckA = groupedByCompany[a].some(truck => representativeTruckImplements.includes(truck.truckImplementId));

      const hasRepresentedTruckB = groupedByCompany[b].some(truck => representativeTruckImplements.includes(truck.truckImplementId));

      if (hasRepresentedTruckA && !hasRepresentedTruckB) return -1;
      if (!hasRepresentedTruckA && hasRepresentedTruckB) return 1;

      return a.localeCompare(b);
    });

    return sortedCompanyNames.map(companyName => ({
      companyName,
      trucks: groupedByCompany[companyName].sort((truckA, truckB) => {
        const isRepresentedA = representativeTruckImplements.includes(truckA.truckImplementId);
        const isRepresentedB = representativeTruckImplements.includes(truckB.truckImplementId);

        if (isRepresentedA && !isRepresentedB) return -1;
        if (!isRepresentedA && isRepresentedB) return 1;

        return truckA.licensePlate.localeCompare(truckB.licensePlate);
      }),
    }));
  }, [truckImplementList, debouncedCompanyFilterValue, debouncedTruckFilterValue, representativeTruckImplements]);

  const handleSelectAll = () => {
    if (selectedTrucks.size === truckImplementList.length) {
      setSelectedTrucks(new Set());
    } else {
      setSelectedTrucks(new Set(truckImplementList.map(truck => truck.truckImplementId)));
    }
  };

  const handleSelectCompany = companyName => {
    const companyTrucks = groupedAndSortedTrucks.find(company => company.companyName === companyName)?.trucks;
    if (!companyTrucks) return;

    const allSelected = companyTrucks.every(truck => selectedTrucks.has(truck.truckImplementId));
    if (allSelected) {
      setSelectedTrucks(prevSelected => {
        const newSelected = new Set(prevSelected);
        companyTrucks.forEach(truck => newSelected.delete(truck.truckImplementId));
        return newSelected;
      });
    } else {
      setSelectedTrucks(prevSelected => {
        const newSelected = new Set(prevSelected);
        companyTrucks.forEach(truck => newSelected.add(truck.truckImplementId));
        return newSelected;
      });
    }
  };

  const handleSelectTruck = truckId => {
    setSelectedTrucks(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(truckId)) {
        newSelected.delete(truckId);
      } else {
        newSelected.add(truckId);
      }
      return newSelected;
    });
  };

  const prepareDataForSave = () => {
    const selectedCompaniesMap = new Map();
    const selectedTrucksData = [];

    truckImplementList.forEach(truck => {
      if (selectedTrucks.has(truck.truckImplementId)) {
        selectedTrucksData.push(truck.truckImplementId);
        if (truck.companyId && !selectedCompaniesMap.has(truck.companyId)) {
          selectedCompaniesMap.set(truck.companyId, truck.companyId);
        }
      }
    });

    return {
      representativeCompanies: Array.from(selectedCompaniesMap.values()),
      representativeTrucks: selectedTrucksData,
    };
  };

  const handleSaveClick = () => {
    const data = prepareDataForSave();
    handleSave(data);
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={true}
        onClose={toggleModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '15px', background: theme.palette.primary.main } }}
        sx={{
          boxShadow:
            'rgba(0, 0, 0, 0.25) 0px 54px 55px, rgba(0, 0, 0, 0.12) 0px -12px 30px, rgba(0, 0, 0, 0.12) 0px 4px 6px, rgba(0, 0, 0, 0.17) 0px 12px 13px, rgba(0, 0, 0, 0.09) 0px -3px 5px',
        }}
      >
        <DialogTitle id="dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {title ? (
            <Typography variant="h6" sx={{ fontWeight: 500, display: 'flex', alignItems: 'baseline', gap: 1 }}>
              {title}
              <Typography component="span" variant="caption" sx={{ opacity: 0.7, fontWeight: 400 }}>
                ({selectedTrucks.size}/{truckImplementList.length} selecionados)
              </Typography>
            </Typography>
          ) : (
            <Typography variant="h6">Selecione as empresas e ativos</Typography>
          )}
          <IconButton onClick={toggleModal}>
            <CloseRounded color="secondary" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {!loading && !!truckImplementList?.length && (
            <>
              <Box display="flex" alignItems="center" justifyContent="space-between" mt={1} mb={2} gap={1}>
                {groupByCompany && (
                  <TextField
                    fullWidth
                    size="small"
                    value={companyFilterValue}
                    onChange={handleCompanyInputChange}
                    placeholder="Buscar por empresa"
                    InputProps={{
                      sx: { borderRadius: '8px', p: '0 8px 2px 0' },
                      endAdornment: (
                        <InputAdornment position="end">
                          {!companyFilterValue ? (
                            <SearchIcon color="action" />
                          ) : (
                            <IconButton size="small" onClick={() => handleCompanyInputChange({ target: { value: '' } })}>
                              <CloseIcon />
                            </IconButton>
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                <TextField
                  fullWidth
                  size="small"
                  value={truckFilterValue}
                  onChange={handleTruckInputChange}
                  placeholder="Buscar por placa"
                  InputProps={{
                    sx: { borderRadius: '8px', p: '0 8px 2px 0' },
                    endAdornment: (
                      <InputAdornment position="end">
                        {!truckFilterValue ? (
                          <SearchIcon color="action" />
                        ) : (
                          <IconButton size="small" onClick={() => handleTruckInputChange({ target: { value: '' } })}>
                            <CloseIcon />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box>
                <FormControlLabel
                  control={<Checkbox checked={selectedTrucks.size === truckImplementList.length} onChange={handleSelectAll} color="secondary" />}
                  label="Selecionar Todos"
                />
              </Box>
            </>
          )}
          <Box sx={{ maxHeight: 500, overflowY: 'auto', mt: 1 }}>
            {filtering || loading ? (
              <Loading />
            ) : groupByCompany ? (
              groupedAndSortedTrucks.map(({ companyName, trucks }) => (
                <Accordion
                  key={companyName}
                  sx={{ my: 1, borderRadius: '8px', '&:before': { display: 'none' } }}
                  defaultExpanded={trucks.some(truck => selectedTrucks.has(truck.truckImplementId))}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      display="flex"
                      alignItems="center"
                      onClick={e => [e.stopPropagation(), handleSelectCompany(companyName)]}
                      style={{ cursor: 'pointer' }}
                      mb={1}
                    >
                      <Checkbox
                        checked={trucks.every(truck => selectedTrucks.has(truck.truckImplementId))}
                        indeterminate={
                          trucks.some(truck => selectedTrucks.has(truck.truckImplementId)) &&
                          !trucks.every(truck => selectedTrucks.has(truck.truckImplementId))
                        }
                        color="secondary"
                      />
                      <Typography color="primary.contrastText">{companyName}</Typography>
                    </Box>
                    <CheckCircleRounded
                      color="success"
                      fontSize="small"
                      sx={{ ml: 1, visibility: trucks.some(truck => selectedTrucks.has(truck.truckImplementId)) ? 'visible' : 'hidden' }}
                    />
                  </AccordionSummary>
                  <AccordionDetails>
                    {trucks.map(truck => (
                      <Box
                        key={truck.truckImplementId}
                        display="flex"
                        alignItems="center"
                        onClick={() => handleSelectTruck(truck.truckImplementId)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Checkbox checked={selectedTrucks.has(truck.truckImplementId)} color="secondary" />
                        <Typography variant="subtitle2">{truck.licensePlate}</Typography>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))
            ) : (
              groupedAndSortedTrucks.reduce((acc, { trucks }) => acc.concat(trucks), [] as any[]).map(truck => (
                <Box
                  key={truck.truckImplementId}
                  display="flex"
                  alignItems="center"
                  onClick={() => handleSelectTruck(truck.truckImplementId)}
                  style={{ cursor: 'pointer' }}
                  sx={{ borderBottom: `1px solid rgba(255,255,255,0.08)`, py: 0.5, '&:last-child': { borderBottom: 'none' } }}
                >
                  <Checkbox checked={selectedTrucks.has(truck.truckImplementId)} color="secondary" />
                  <Typography variant="subtitle2">{truck.licensePlate}</Typography>
                </Box>
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '8px 24px', my: 2 }}>
          <Button onClick={toggleModal} disableRipple variant="text" sx={{ color: 'text.primary' }}>
            {translate('fleetSenseApp.truckDeviceStatus.frontEnd.buttonNames.cancel')}
          </Button>
          <Button onClick={handleSaveClick} disableRipple variant="contained" color="secondary">
            {translate('fleetSenseApp.truckDeviceStatus.frontEnd.buttonNames.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};
